import * as React from "react";

import "./Main.css";

import { ContentType, HttpVerb, requestWithAuth } from "../../../utils/httpRequest";
import api from "../../api";

import PartnerData from "../../../classes/Partner";
import Partner from "../../../classes/Partner";
import CompanyCache from "../../../classes/CompanyCache";
import CompanyData, { EnrichmentStatus } from "../../../classes/Company";

import AppContext from "../AppContext";
import ContactPage from "../Contact/ContactPage/ContactPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Search, { SearchState } from "../Search/Search";
import RecipientChooser, { RecipientCandidate } from "../RecipientChooser/RecipientChooser";
import CompanyChooser, { CompanyContextCandidate } from "../CompanyChooser/CompanyChooser";
import Lead from "../../../classes/Lead";
import {
  faArrowLeft,
  faBuilding,
  faChartLine,
  faPlusCircle,
  faRedoAlt,
  faSearch,
  faTicketAlt,
  faUser,
  faUsers,
  faUserPlus
} from "@fortawesome/free-solid-svg-icons";
import EnrichmentInfo, { EnrichmentInfoType } from "../../../classes/EnrichmentInfo";
import Progress from "../GrayOverlay";
import { TooltipHost } from "office-ui-fabric-react";
import { _t } from "../../../utils/Translator";
import MicrosoftAuthStatus from "../MicrosoftAuthStatus/MicrosoftAuthStatus";

type MainProps = {
  canCreatePartner: boolean;
};

type MainState = {
  matchedPartners: Partner[];
  partnersLoading: boolean;
  translationsLoading: boolean;
  searchQuery: string;
  selectedPartner: Partner;
  selectedRecipientCandidate?: RecipientCandidate;
  recipientCandidates: RecipientCandidate[];
  companyCandidates: CompanyContextCandidate[];
  isChoosingRecipient: boolean;
  isChoosingCompany: boolean;
  isCompanyContext: boolean;
  contactPartner?: Partner;
  isSearching: boolean;
  backStack: BackStackItem[];
  poppedElement: BackStackItem;
  contactKey: number; //used for contact refresh
  loadPartner: boolean;
};

enum BackStackItemType {
  partner,
  query
}

type BackStackItem = {
  type: BackStackItemType;
  element: string | Partner;
  historyState?: any;
};

type ResolvedMailboxParticipants = {
  currentUserEmail: string | null;
  sender?: RecipientCandidate;
  recipients: RecipientCandidate[];
  autoOpenCandidates: RecipientCandidate[];
  defaultCandidate?: RecipientCandidate;
  fallbackCandidate?: RecipientCandidate;
};

class Main extends React.Component<MainProps, MainState> {
  companyCache: CompanyCache;

  constructor(props) {
    super(props);
    this.state = {
      matchedPartners: undefined,
      searchQuery: undefined,
      selectedPartner: undefined,
      selectedRecipientCandidate: undefined,
      recipientCandidates: [],
      companyCandidates: [],
      isChoosingRecipient: false,
      isChoosingCompany: false,
      isCompanyContext: false,
      contactPartner: undefined,
      isSearching: false,
      partnersLoading: true,
      translationsLoading: true,
      backStack: [],
      poppedElement: undefined,
      contactKey: Math.random(),
      loadPartner: true
    };

    this.companyCache = new CompanyCache(2, 200, 0.25);
  }

  componentDidMount() {
    this.initializePartnerContext();
  }

  private addPartnerToDbRequest = () => {
    if (!this.context.isConnected()) {
      this.context.navigation.goToLogin();
      return;
    }

    this.setState({ partnersLoading: true });

    const addPartnerRequest = requestWithAuth(
      HttpVerb.POST,
      api.baseURL + api.createPartner,
      ContentType.Json,
      {
        name: this.state.selectedPartner.name,
        email: this.state.selectedPartner.email
      }
    );

    addPartnerRequest.promise
      .then(({ responseText }) => {
        const parsed = JSON.parse(responseText);
        const newId = parsed.id;
        let partner = this.state.selectedPartner;
        partner.id = newId;
        this.setState({ selectedPartner: partner, partnersLoading: false });
        this.onRefreshPartnerClick();
      })
      .catch(error => {
        console.log(error);
        this.setState({ partnersLoading: false });
        this.context.showHttpErrorMessage(error);
      });
  };

  private initializePartnerContext = async () => {
    if (!Office.context.mailbox.item) {
      this.setState({ partnersLoading: false, translationsLoading: false });
      return;
    }

    try {
      const resolvedParticipants = await this.resolveMailboxParticipants();
      const nextState: Partial<MainState> = {
        recipientCandidates: resolvedParticipants.recipients,
        selectedRecipientCandidate: resolvedParticipants.defaultCandidate || resolvedParticipants.fallbackCandidate,
        companyCandidates: [],
        isChoosingCompany: false,
        isCompanyContext: false,
        contactPartner: undefined,
        translationsLoading: false
      };

      if (resolvedParticipants.autoOpenCandidates.length > 1) {
        this.setState({
          ...nextState,
          partnersLoading: false,
          isChoosingRecipient: true,
          isChoosingCompany: false,
          isSearching: false,
          selectedPartner: undefined,
          loadPartner: false
        } as Pick<
          MainState,
          | "recipientCandidates"
          | "selectedRecipientCandidate"
          | "translationsLoading"
          | "partnersLoading"
          | "isChoosingRecipient"
          | "isChoosingCompany"
          | "isSearching"
          | "selectedPartner"
          | "loadPartner"
        >);
        this.preloadRecipientCandidatePreviews(resolvedParticipants.recipients);
        return;
      }

      const candidate = resolvedParticipants.defaultCandidate || resolvedParticipants.fallbackCandidate;
      if (!candidate?.email) {
        this.setState({ partnersLoading: false, isChoosingRecipient: false, translationsLoading: false });
        return;
      }

      this.setState(
        {
          ...nextState,
          partnersLoading: true,
          isChoosingRecipient: false,
          isChoosingCompany: false
        } as Pick<
          MainState,
          | "recipientCandidates"
          | "selectedRecipientCandidate"
          | "translationsLoading"
          | "partnersLoading"
          | "isChoosingRecipient"
          | "isChoosingCompany"
        >,
        () => {
          this.preloadRecipientCandidatePreviews(resolvedParticipants.recipients);
          this.loadPartnerForCandidate(candidate);
        }
      );
    } catch (error) {
      this.context.showHttpErrorMessage(error);
      this.setState({ partnersLoading: false, translationsLoading: false });
    }
  };

  private selectPartnerForCandidate = (
    partners: Partner[],
    candidate: RecipientCandidate
  ): void => {
    const selectedPartner = partners[0];
    const companyCandidate = this.getCompanyCandidateFromPartner(selectedPartner, candidate.email);

    this.setState(previousState => {
      if (
        previousState.selectedRecipientCandidate?.email &&
        previousState.selectedRecipientCandidate.email !== candidate.email
      ) {
        return null;
      }
      const candidateWithPartner: RecipientCandidate = {
        ...candidate,
        image: selectedPartner.image || candidate.image,
        partnerId: selectedPartner.id > 0 ? selectedPartner.id : candidate.partnerId,
        partner: selectedPartner
      };

      return {
        matchedPartners: partners,
        selectedPartner,
        contactPartner: selectedPartner.isCompany ? undefined : selectedPartner,
        isCompanyContext: selectedPartner.isCompany,
        companyCandidates: this.mergeCompanyCandidates(previousState.companyCandidates, [companyCandidate]),
        recipientCandidates: previousState.recipientCandidates.map(existingCandidate =>
          existingCandidate.email === candidate.email ? candidateWithPartner : existingCandidate
        ),
        selectedRecipientCandidate: candidateWithPartner,
        partnersLoading: false
      };
    });
  };

  private getAllMatchedPartnersRequest = async (candidate: RecipientCandidate) => {
    if (!Office.context.mailbox.item) {
      return;
    }

    const email = candidate.email?.toLowerCase();
    const displayName = candidate.displayName;

    if (!email) { 
      this.setState({ partnersLoading: false });
      return;
    }

    const CancellableMatchedPartnersRequest = requestWithAuth(
      HttpVerb.PATCH,
      api.baseURL + api.searchPartner,
      ContentType.Json,
      {
        args: [[["email", "=", email.toLowerCase()]]]
      }
    );

    this.context.addRequestCanceller(CancellableMatchedPartnersRequest.cancel);
      CancellableMatchedPartnersRequest.promise
        .then(({ responseText }) => {
          let partners = [];
          if (responseText) {
            const parsed = JSON.parse(responseText);
            
            // Normalize Odoo boolean values
            const normalizeBoolean = (value: any): boolean => {
              if (value === true || value === 'True' || value === 'true' || value === 1) return true;
              if (value === false || value === 'False' || value === 'false' || value === 0) return false;
              return Boolean(value);
            };
            
            const isSuccess = normalizeBoolean(parsed.success);
            
            // Check for error response from backend
            if (!isSuccess && parsed.error) {
              this.context.showHttpErrorMessage(parsed);
              this.setState({ partnersLoading: false });
              return;
            }
            
            if (isSuccess && Array.isArray(parsed.data) && parsed.data.length > 0) {
              partners = parsed.data.map(partner_json => {
                return PartnerData.fromJSON(partner_json);
              });
              partners = Partner.sortBestMatches(email, displayName, partners);
              this.selectPartnerForCandidate(partners, candidate);
            } else {
              // No partners found (empty array, no data, or invalid response) - create a new partner
              const newPartner = PartnerData.createNewPartnerFromEmail(displayName, email);
              partners.push(newPartner);
              this.selectPartnerForCandidate(partners, candidate);
            }
          } else {
            // No partners were found in database, we create a new partner
            const newPartner = PartnerData.createNewPartnerFromEmail(displayName, email);
            partners.push(newPartner);
            this.selectPartnerForCandidate(partners, candidate);
          }
        })
      .catch(error => {
        this.context.showHttpErrorMessage(error);
        this.setState({ partnersLoading: false });
        console.log(error);
      });
  };

  private getCompanyCandidateFromPartner = (
    partner: Partner,
    sourceEmail?: string
  ): CompanyContextCandidate | undefined => {
    const company = partner?.company;
    if (!company || !company.id || company.id <= 0) return undefined;

    return {
      id: company.id,
      name: company.getName() || partner.name,
      email: company.email || undefined,
      image: company.image || undefined,
      sourceEmails: sourceEmail ? [sourceEmail] : [],
      contact: partner.isCompany ? undefined : partner
    };
  };

  private mergeCompanyCandidates = (
    existing: CompanyContextCandidate[],
    additions: Array<CompanyContextCandidate | undefined>
  ): CompanyContextCandidate[] => {
    const merged = existing.map(candidate => ({ ...candidate, sourceEmails: [...candidate.sourceEmails] }));

    additions.filter(Boolean).forEach(addition => {
      const candidate = addition as CompanyContextCandidate;
      const current = merged.find(existingCandidate => existingCandidate.id === candidate.id);
      if (current) {
        current.name = current.name || candidate.name;
        current.email = current.email || candidate.email;
        current.image = current.image || candidate.image;
        current.contact = current.contact || candidate.contact;
        current.sourceEmails = Array.from(new Set([...current.sourceEmails, ...candidate.sourceEmails]));
      } else {
        merged.push({ ...candidate, sourceEmails: [...candidate.sourceEmails] });
      }
    });

    return merged;
  };

  private fetchBestPartnerPreview = async (candidate: RecipientCandidate): Promise<Partner | undefined> => {
    if (candidate.partner) return candidate.partner;

    const previewRequest = requestWithAuth(
      HttpVerb.PATCH,
      api.baseURL + api.searchPartner,
      ContentType.Json,
      { args: [[["email", "=", candidate.email.toLowerCase()]]] }
    );
    this.context.addRequestCanceller(previewRequest.cancel);

    try {
      const { responseText } = await previewRequest.promise;
      if (!responseText) return undefined;
      const parsed = JSON.parse(responseText);
      const isSuccess = parsed.success === true || parsed.success === "True" || parsed.success === "true" || parsed.success === 1;
      if (!isSuccess || !Array.isArray(parsed.data) || parsed.data.length === 0) return undefined;
      const partners = parsed.data.map(partnerJson => PartnerData.fromJSON(partnerJson));
      return Partner.sortBestMatches(candidate.email, candidate.displayName, partners)[0];
    } catch (error) {
      console.warn("Could not preload recipient preview.", candidate.email, error);
      return undefined;
    }
  };

  private preloadRecipientCandidatePreviews = async (candidates: RecipientCandidate[]): Promise<Partner[]> => {
    if (!this.context.isConnected() || !candidates || candidates.length === 0) return [];

    const previews = await Promise.all(
      candidates.map(async candidate => ({ candidate, partner: await this.fetchBestPartnerPreview(candidate) }))
    );
    const availablePreviews = previews.filter(preview => Boolean(preview.partner));

    this.setState(previousState => {
      const activeEmails = new Set(previousState.recipientCandidates.map(candidate => candidate.email));
      const activePreviews = availablePreviews.filter(preview => activeEmails.has(preview.candidate.email));
      const previewByEmail = new Map<string, Partner>();
      activePreviews.forEach(preview => previewByEmail.set(preview.candidate.email, preview.partner));
      const updateCandidate = (candidate: RecipientCandidate): RecipientCandidate => {
        const bestMatch = previewByEmail.get(candidate.email);
        if (!bestMatch) return candidate;
        return {
          ...candidate,
          image: bestMatch.image || candidate.image,
          partnerId: bestMatch.id || candidate.partnerId,
          partner: bestMatch
        };
      };

      const companyCandidates = this.mergeCompanyCandidates(
        previousState.companyCandidates,
        activePreviews.map(preview =>
          this.getCompanyCandidateFromPartner(preview.partner, preview.candidate.email)
        )
      );

      return {
        recipientCandidates: previousState.recipientCandidates.map(updateCandidate),
        selectedRecipientCandidate: previousState.selectedRecipientCandidate
          ? updateCandidate(previousState.selectedRecipientCandidate)
          : previousState.selectedRecipientCandidate,
        companyCandidates
      };
    });

    return availablePreviews.map(preview => preview.partner);
  };

  private normalizeEmail = (email?: string | null): string | null => {
    if (!email) return null;
    const normalizedEmail = email.trim().toLowerCase();
    return normalizedEmail || null;
  };

  private normalizeDisplayName = (displayName?: string | null, email?: string | null): string => {
    const normalizedName = (displayName || "").trim();
    if (normalizedName) return normalizedName;
    return email || "";
  };

  private buildRecipientCandidate = (
    recipient: { emailAddress?: string; displayName?: string } | null | undefined,
    source: "to" | "cc" | "sender"
  ): RecipientCandidate | null => {
    const normalizedEmail = this.normalizeEmail(recipient?.emailAddress);
    if (!normalizedEmail) return null;

    return {
      email: normalizedEmail,
      displayName: this.normalizeDisplayName(recipient?.displayName, normalizedEmail),
      source,
      isExternal: true
    };
  };

  private getRecipientsForField = (fieldName: "to" | "cc"): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const item: any = Office.context.mailbox.item;
      const field = item?.[fieldName];

      if (Array.isArray(field)) {
        resolve(field);
        return;
      }

      if (field && typeof field.getAsync === "function") {
        field.getAsync(result => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value || []);
          } else {
            reject(result.error);
          }
        });
        return;
      }

      resolve([]);
    });
  };

  private resolveMailboxParticipants = async (): Promise<ResolvedMailboxParticipants> => {
    const item: any = Office.context.mailbox.item;
    const currentUserEmail = this.normalizeEmail(Office.context.mailbox.userProfile?.emailAddress);
    
    // In compose mode, item.from is an async object (has getAsync method) - we ARE the sender, skip it
    const isComposeMode = typeof item?.from?.getAsync === 'function';
    const sender = isComposeMode
      ? null
      : this.buildRecipientCandidate(item?.from || item?.sender, "sender");
    
    const isCurrentUserSender = Boolean(currentUserEmail && sender?.email === currentUserEmail);

    const [toRecipients, ccRecipients] = await Promise.all([
      this.getRecipientsForField("to"),
      this.getRecipientsForField("cc")
    ]);

    const allRecipients = [
      ...toRecipients.map(recipient => this.buildRecipientCandidate(recipient, "to")),
      ...ccRecipients.map(recipient => this.buildRecipientCandidate(recipient, "cc"))
    ].filter(Boolean) as RecipientCandidate[];

    const uniqueRecipients = allRecipients.reduce((accumulator, recipient) => {
      if (!recipient.email) {
        return accumulator;
      }

      // In read mode, skip current user from recipients ONLY if there are other recipients
      // (e.g. sent items where all recipients are the current user). 
      // If the current user is the only recipient, include them (user may email themselves).
      const hasOtherRecipients = allRecipients.some(r => r.email && r.email !== currentUserEmail);
      if (!isComposeMode && currentUserEmail && recipient.email === currentUserEmail && hasOtherRecipients) {
        return accumulator;
      }

      if (!accumulator.some(existingRecipient => existingRecipient.email === recipient.email)) {
        accumulator.push(recipient);
      }

      return accumulator;
    }, [] as RecipientCandidate[]);

    const senderCandidate =
      sender && sender.email && sender.email !== currentUserEmail
        ? {
            ...sender,
            source: "sender" as const
          }
        : undefined;

    const primaryRecipients = uniqueRecipients.filter(recipient => recipient.source === "to");
    const autoOpenCandidates = (
      isCurrentUserSender
        ? [
            ...(senderCandidate ? [senderCandidate] : []),
            ...primaryRecipients
          ]
        : senderCandidate
          ? [senderCandidate]
          : []
    ).reduce((accumulator, recipient) => {
      if (!accumulator.some(existingRecipient => existingRecipient.email === recipient.email)) {
        accumulator.push(recipient);
      }
      return accumulator;
    }, [] as RecipientCandidate[]);

    const selectableCandidates = [
      ...(senderCandidate ? [senderCandidate] : []),
      ...uniqueRecipients
    ].reduce((accumulator, recipient) => {
      if (!accumulator.some(existingRecipient => existingRecipient.email === recipient.email)) {
        accumulator.push(recipient);
      }
      return accumulator;
    }, [] as RecipientCandidate[]);

    const defaultCandidate = selectableCandidates.length === 1 ? selectableCandidates[0] : undefined;

    const fallbackCandidate =
      autoOpenCandidates[0] ||
      selectableCandidates[0] ||
      sender;

    return {
      currentUserEmail,
      sender,
      recipients: selectableCandidates,
      autoOpenCandidates,
      defaultCandidate,
      fallbackCandidate
    };
  };

  private loadPartnerForCandidate = (candidate: RecipientCandidate) => {
    if (!candidate?.email) {
      this.setState({ partnersLoading: false, isChoosingRecipient: false });
      return;
    }

    this.setState({
      partnersLoading: true,
      isChoosingRecipient: false,
      isChoosingCompany: false,
      isCompanyContext: false,
      contactPartner: undefined,
      isSearching: false,
      selectedRecipientCandidate: candidate,
      selectedPartner: undefined,
      loadPartner: true
    });

    if (this.context.isConnected()) {
      this.getAllMatchedPartnersRequest(candidate);
    } else {
      this.getPartnerDisconnectedRequest(candidate);
    }
  };

  private onRecipientCandidateSelected = (candidate: RecipientCandidate) => {
    this.setState({
      backStack: [],
      poppedElement: undefined,
      searchQuery: undefined
    });
    this.loadPartnerForCandidate(candidate);
  };

  private onChangeRecipientClick = () => {
    if (!this.state.recipientCandidates || this.state.recipientCandidates.length <= 1) {
      return;
    }

    this.preloadRecipientCandidatePreviews(this.state.recipientCandidates);
    this.setState({
      isChoosingRecipient: true,
      isChoosingCompany: false,
      isCompanyContext: false,
      contactPartner: undefined,
      isSearching: false,
      selectedPartner: undefined,
      loadPartner: false,
      backStack: [],
      poppedElement: undefined
    });
  };

  private switchToCompanyCandidate = (candidate: CompanyContextCandidate): void => {
    const currentContact = candidate.contact ||
      (!this.state.isCompanyContext && this.state.selectedPartner && !this.state.selectedPartner.isCompany
        ? this.state.selectedPartner
        : this.state.contactPartner);
    const companyPartner = PartnerData.fromJSON({
      id: candidate.id,
      name: candidate.name,
      email: candidate.email || "",
      image_1920: candidate.image || false,
      is_company: true
    });
    const sourceRecipient = currentContact?.email
      ? this.state.recipientCandidates.find(recipient => recipient.email === currentContact.email)
      : undefined;

    this.setState({
      selectedPartner: companyPartner,
      selectedRecipientCandidate: sourceRecipient || this.state.selectedRecipientCandidate,
      contactPartner: currentContact,
      isCompanyContext: true,
      isChoosingCompany: false,
      isChoosingRecipient: false,
      partnersLoading: false,
      contactKey: Math.random(),
      loadPartner: true,
      backStack: [],
      poppedElement: undefined
    });
  };

  private onCompanyContextClick = async (): Promise<void> => {
    this.setState({ partnersLoading: true });
    const previewPartners = await this.preloadRecipientCandidatePreviews(this.state.recipientCandidates);
    const currentPartner = this.state.selectedPartner;
    const additions = previewPartners.map(partner =>
      this.getCompanyCandidateFromPartner(partner, partner.email)
    );
    if (currentPartner) {
      additions.push(this.getCompanyCandidateFromPartner(currentPartner, currentPartner.email));
    }
    const companyCandidates = this.mergeCompanyCandidates(this.state.companyCandidates, additions);

    if (companyCandidates.length === 0) {
      this.setState({ partnersLoading: false, companyCandidates });
      return;
    }

    if (companyCandidates.length === 1) {
      this.setState({ companyCandidates }, () => this.switchToCompanyCandidate(companyCandidates[0]));
      return;
    }

    this.setState({
      companyCandidates,
      partnersLoading: false,
      isChoosingCompany: true,
      isChoosingRecipient: false,
      isSearching: false,
      selectedPartner: undefined,
      loadPartner: false,
      backStack: [],
      poppedElement: undefined
    });
  };

  private onCompanyCandidateSelected = (candidate: CompanyContextCandidate): void => {
    this.switchToCompanyCandidate(candidate);
  };

  private onContactContextClick = (): void => {
    const contactPartner = this.state.contactPartner;
    if (!contactPartner) return;
    const sourceRecipient = this.state.recipientCandidates.find(
      recipient => recipient.email === contactPartner.email
    );
    this.setState({
      selectedPartner: contactPartner,
      selectedRecipientCandidate: sourceRecipient || this.state.selectedRecipientCandidate,
      isCompanyContext: false,
      isChoosingCompany: false,
      partnersLoading: false,
      contactKey: Math.random(),
      loadPartner: true
    });
  };
  

  // private getTranslations = () => {
  //   this.setState({ translationsLoading: true });
  //   const requestPromise = sendHttpRequest(
  //     HttpVerb.POST,
  //     api.baseURL + api.getTranslations,
  //     ContentType.Json,
  //     this.context.getConnectionToken(),
  //     {
  //       plugin: "outlook"
  //     }
  //   ).promise;
  //   requestPromise
  //     .then(response => {
  //       const parsed = JSON.parse(response);
  //       let translations = parsed.result;
  //       saveTranslations(translations);
  //     })
  //     .finally(() => {
  //       this.setState({ translationsLoading: false });
  //     });
  // };

  private getPartnerDisconnectedRequest = (candidate: RecipientCandidate) => {
    if (!Office.context.mailbox.item) {
      return;
    }
    Office.context.mailbox.getUserIdentityTokenAsync(async idTokenResult => {
      const email = candidate.email;
      const displayName = candidate.displayName;

      if (!email) { 
        this.setState({ partnersLoading: false });
        return;
      }

      const partner = PartnerData.createNewPartnerFromEmail(displayName, email) as Partner;

      const cachedCompany: CompanyData = this.companyCache.get(email);

      if (cachedCompany) {
        partner.company = cachedCompany;
        partner.company.enrichmentStatus = EnrichmentStatus.enriched;
        this.setState({
          matchedPartners: [partner],
          selectedPartner: partner,
          contactPartner: partner,
          isCompanyContext: false,
          partnersLoading: false
        });
      } else {
        const senderDomain = email.split("@")[1];
        const cancellableRequest = requestWithAuth(
          HttpVerb.POST,
          api.iapLeadEnrichment,
          ContentType.Json,
          {
            params: {
              email: email,
              domain: senderDomain,
              extuid: idTokenResult.value
            }
          },
          { unauthenticated: true }
        );
        this.context.addRequestCanceller(cancellableRequest.cancel);
        cancellableRequest.promise
          .then(({ responseText }) => {
            const parsed = JSON.parse(responseText);
            if ("error" in parsed.result) {
              const enrichmentInfo = new EnrichmentInfo(parsed.result.error.type, parsed.result.error.info);
              if (enrichmentInfo.type != EnrichmentInfoType.NoData) this.context.showTopBarMessage(enrichmentInfo);
              partner.company = CompanyData.getEmptyCompany();
              partner.company.enrichmentStatus = EnrichmentStatus.enrichmentEmpty;
              this.setState({
                matchedPartners: [partner],
                selectedPartner: partner,
                contactPartner: partner,
                isCompanyContext: false,
                partnersLoading: false
              });
              return;
            }
            partner.company = CompanyData.fromRevealJSON(parsed.result);
            this.companyCache.add(partner.company);
            partner.company.enrichmentStatus = EnrichmentStatus.enriched;
            this.setState({
              matchedPartners: [partner],
              selectedPartner: partner,
              contactPartner: partner,
              isCompanyContext: false,
              partnersLoading: false
            });
          })
          .catch(error => {
            this.context.showHttpErrorMessage(error);
            this.setState({ partnersLoading: false });
          });
      }
    });
  };

  private onSearchClick = state => {
    if (this.context.isConnected()) {
      const backStackItem = {
        type: BackStackItemType.partner,
        element: this.state.selectedPartner,
        historyState: state
      } as BackStackItem;
      if (this.state.backStack?.length > 0 || this.state.matchedPartners?.length == 1) {
        this.setState({ isSearching: true, searchQuery: "" });
      } else {
        this.setState({
          isSearching: true,
          searchQuery: this.state.selectedPartner?.email
        });
      }

      this.pushItemBackStack(backStackItem);
    } else {
      this.context.navigation.goToLogin();
    }
  };

  private onSearchPartnerItemClick = (partner: Partner, state: SearchState) => {
    const backStackItem = {
      type: BackStackItemType.query,
      element: state.query,
      historyState: state
    } as BackStackItem;
    this.pushItemBackStack(backStackItem);
    this.setState({
      isSearching: false,
      selectedPartner: partner,
      contactPartner: partner.isCompany ? undefined : partner,
      isCompanyContext: partner.isCompany,
      isChoosingCompany: false,
      companyCandidates: this.mergeCompanyCandidates(
        this.state.companyCandidates,
        [this.getCompanyCandidateFromPartner(partner, partner.email)]
      ),
      contactKey: Math.random(),
      loadPartner: true
    });
  };

  private buildVirtualPartnerFromLead = (lead: Lead): Partner => {
    const fallbackName = lead.name || _t("Lead");
    const fallbackEmail = lead.emailFrom || "";
    const virtualPartner = PartnerData.createNewPartnerFromEmail(fallbackName, fallbackEmail) as Partner;
    if (lead.partnerId && lead.partnerId > 0) {
      virtualPartner.id = lead.partnerId;
    }
    virtualPartner.leads = [Lead.copy(lead)];
    return virtualPartner;
  };

  private onSearchLeadItemClick = (lead: Lead, state: SearchState) => {
    const backStackItem = {
      type: BackStackItemType.query,
      element: state.query,
      historyState: state
    } as BackStackItem;
    this.pushItemBackStack(backStackItem);

    const virtualPartner = this.buildVirtualPartnerFromLead(lead);
    const canLoadPartnerData = Boolean((virtualPartner.id && virtualPartner.id > 0) || virtualPartner.email);

    this.setState({
      isSearching: false,
      selectedPartner: virtualPartner,
      contactPartner: virtualPartner,
      isCompanyContext: false,
      isChoosingCompany: false,
      contactKey: Math.random(),
      loadPartner: canLoadPartnerData
    });
  };

  private onRefreshPartnerClick = () => {
    // In compose mode, always re-read TO recipients (user may have changed them)
    if (this.context.isComposeMode) {
      this.setState({
        partnersLoading: true,
        selectedPartner: undefined,
        selectedRecipientCandidate: undefined,
        contactPartner: undefined,
        companyCandidates: [],
        isCompanyContext: false,
        isChoosingCompany: false
      });
      this.initializePartnerContext();
      return;
    }

    if (this.state.selectedPartner) {
      this.setState({ contactKey: Math.random(), loadPartner: true });
    } else if (this.state.selectedRecipientCandidate) {
      this.loadPartnerForCandidate(this.state.selectedRecipientCandidate);
    } else {
      this.initializePartnerContext();
    }
  };

  private onBackClicked = () => {
    let backStack = [...this.state.backStack];
    const backStackItem = backStack.pop();
    if (backStackItem.type == BackStackItemType.partner) {
      const partner = backStackItem.element as Partner;
      this.setState({
        isSearching: false,
        selectedPartner: partner,
        contactPartner: partner.isCompany ? undefined : partner,
        isCompanyContext: partner.isCompany,
        isChoosingCompany: false,
        poppedElement: backStackItem,
        backStack: backStack,
        loadPartner: false
      });
    } else {
      const query = backStackItem.element as string;
      this.setState({
        isSearching: true,
        searchQuery: query,
        poppedElement: backStackItem,
        backStack: backStack
      });
    }
    this.onRefreshPartnerClick();
  };

  private pushItemBackStack = (item: BackStackItem) => {
    let backStack = [...this.state.backStack];
    backStack.push(item);
    this.setState({ backStack: backStack });
  };

  private updatePartner = (partner: Partner) => {
    this.setState(previousState => ({
      selectedPartner: partner,
      contactPartner: previousState.isCompanyContext ? previousState.contactPartner : partner,
      companyCandidates: this.mergeCompanyCandidates(
        previousState.companyCandidates,
        [this.getCompanyCandidateFromPartner(partner, partner.email)]
      )
    }));
  };

  render() {
    if (this.state.partnersLoading) {
      return <Progress />;
    }

    let topBarContent = null;
    let backButton = null;

    if (this.state.backStack.length != 0) {
      backButton = (
        <div className="odoo-muted-button" onClick={this.onBackClicked} style={{ border: "none" }}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </div>
      );
    }

    let broadCampStyle = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: "medium",
      color: "#787878",
      fontWeight: 600
    };

    if (this.state.isSearching) {
      topBarContent = (
        <div style={broadCampStyle}>
          {backButton}
          <div>{_t("Search In Database")}</div>
          <span />
        </div>
      );
    } else if (this.state.isChoosingRecipient) {
      topBarContent = (
        <div style={broadCampStyle}>
          {backButton}
          <div>{_t("Choose Contact")}</div>
          <span />
        </div>
      );
    } else if (this.state.isChoosingCompany) {
      topBarContent = (
        <div style={broadCampStyle}>
          {backButton}
          <div>{_t("Choose Company")}</div>
          <span />
        </div>
      );
    } else {
      let refrechPartnerButton = null;
      let addPartnerButton = null;
      let changeRecipientButton = null;
      let contextSwitchButton = null;

      if (this.context.isConnected()) {
        refrechPartnerButton = (
          <TooltipHost content={_t("Refresh Contact")}>
            <div className="odoo-muted-button" onClick={this.onRefreshPartnerClick} style={{ border: "none" }}>
              <FontAwesomeIcon icon={faRedoAlt} style={{ cursor: "pointer" }} />
            </div>
          </TooltipHost>
        );
      }

      if (this.state.recipientCandidates.length > 1) {
        changeRecipientButton = (
          <TooltipHost content={_t("Change Recipient")}>
            <div className="odoo-muted-button" onClick={this.onChangeRecipientClick} style={{ border: "none" }}>
              <FontAwesomeIcon icon={faUsers} style={{ cursor: "pointer" }} />
            </div>
          </TooltipHost>
        );
      }

      const selectedCompany = this.state.selectedPartner
        ? this.getCompanyCandidateFromPartner(this.state.selectedPartner, this.state.selectedPartner.email)
        : undefined;
      const hasCompanyContext = Boolean(selectedCompany || this.state.companyCandidates.length > 0);
      if (this.state.isCompanyContext && this.state.contactPartner) {
        contextSwitchButton = (
          <TooltipHost content={_t("Switch to Contact")}>
            <div className="odoo-muted-button" onClick={this.onContactContextClick} style={{ border: "none" }}>
              <FontAwesomeIcon icon={faUser} style={{ cursor: "pointer" }} />
            </div>
          </TooltipHost>
        );
      } else if (!this.state.isCompanyContext && hasCompanyContext) {
        contextSwitchButton = (
          <TooltipHost content={_t("Switch to Company")}>
            <div className="odoo-muted-button" onClick={this.onCompanyContextClick} style={{ border: "none" }}>
              <FontAwesomeIcon icon={faBuilding} style={{ cursor: "pointer" }} />
            </div>
          </TooltipHost>
        );
      }

      if (
        this.state.selectedPartner &&
        !(this.state.selectedPartner.id && this.state.selectedPartner.id > 0) &&
        this.props.canCreatePartner
      ) {
        addPartnerButton = (
          <TooltipHost content={_t("Add Contact To Database")}>
            <div className="odoo-muted-button" onClick={this.addPartnerToDbRequest} style={{ border: "none" }}>
              <FontAwesomeIcon icon={faPlusCircle} style={{ cursor: "pointer" }} />
            </div>
          </TooltipHost>
        );
      }

      topBarContent = (
        <div style={broadCampStyle}>
          {backButton}
          <div>{_t(this.state.isCompanyContext ? "Company Details" : "Contact Details")}</div>
          <div style={{ display: "flex" }}>
            {contextSwitchButton}
            {changeRecipientButton}
            <TooltipHost content={_t("Search In Odoo")}>
              <div className="odoo-muted-button" onClick={this.onSearchClick} style={{ border: "none" }}>
                <FontAwesomeIcon icon={faSearch} style={{ cursor: "pointer" }} />
              </div>
            </TooltipHost>
            {refrechPartnerButton}
            {addPartnerButton}
          </div>
        </div>
      );
    }

    let topBar = <div style={{ margin: "8px" }}>{topBarContent}</div>;

    let connectionButton = (
      <div className="link-like-button connect-button" onClick={() => this.context.navigation.goToLogin()}>
        Connect
      </div>
    );
    if (this.context.isConnected()) {
      connectionButton = (
        <div
          className="link-like-button connect-button"
          onClick={() => {
            this.context.disconnect();
            this.context.navigation.goToLogin();
          }}
        >
          {_t("Disconnect")}
        </div>
      );
    }

    let mainContent = null;
    const showFirstRunIntro =
      !this.context.isConnected() &&
      !this.state.isChoosingRecipient &&
      !this.state.isChoosingCompany &&
      !this.state.selectedPartner;
    let firstRunIntro = null;

    if (showFirstRunIntro) {
      firstRunIntro = (
        <section className="first-run-intro">
          <div className="first-run-intro__header">
            <div>
              <h2>Bring Odoo context into every Outlook email</h2>
              <p>
                Reply faster with CRM insights, open cases, and account history. No more tab switching or guessing.
              </p>
            </div>
          </div>

          <ul className="first-run-intro__benefits">
            <li>
              <FontAwesomeIcon icon={faChartLine} />
              <span>See contact context—role, company insights, and open records—right beside the email.</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faTicketAlt} />
              <span>Monitor open helpdesk tickets and commitments without leaving your inbox.</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faUserPlus} />
              <span>Create or update Odoo contacts in one click so every follow-up stays in sync.</span>
            </li>
          </ul>

          <span>Use your Odoo URL to get started in under a minute.</span>
        </section>
      );
    }

    if (this.state.isSearching) {
      mainContent = (
        <div>
          <Search
            query={this.state.searchQuery}
            canCreatePartner={this.props.canCreatePartner}
            historyState={
              this.state.poppedElement && this.state.poppedElement.type == BackStackItemType.query
                ? this.state.poppedElement.historyState
                : undefined
            }
            onPartnerClick={this.onSearchPartnerItemClick}
            onLeadClick={this.onSearchLeadItemClick}
          />
        </div>
      );
    } else if (this.state.isChoosingRecipient) {
      mainContent = (
        <RecipientChooser
          candidates={this.state.recipientCandidates}
          selectedCandidate={this.state.selectedRecipientCandidate}
          onSelect={this.onRecipientCandidateSelected}
        />
      );
    } else if (this.state.isChoosingCompany) {
      mainContent = (
        <CompanyChooser
          candidates={this.state.companyCandidates}
          selectedCompanyId={this.state.isCompanyContext ? this.state.selectedPartner?.id : undefined}
          onSelect={this.onCompanyCandidateSelected}
        />
      );
    } else {
      if (this.state.selectedPartner) {
        mainContent = (
          <>
            <ContactPage
              partner={this.state.selectedPartner}
              onPartnerChanged={this.updatePartner}
              loadPartner={this.state.loadPartner}
              key={this.state.contactKey}
            />
          </>
        );
      }
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div>{topBar}</div>
        <MicrosoftAuthStatus />
        <div>{mainContent}</div>
        {firstRunIntro}
        <div style={{ flex: 1 }}>{connectionButton}</div>
        <footer className="footer-home">
          <img src="assets/2BIT.png" style={{ height: "35px", marginRight: "5px" }} />
          <div>Powered by <a href="https://www.2bit.ch" target="_blank" style={{ color: "black" }}>2BIT AG</a></div>
        </footer>
      </div>
    );
  }
}
Main.contextType = AppContext;

export default Main;
